import React, { useRef, useState } from "react";
import { Share2, Download, Copy, X, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Generates a shareable ranking poster (1080x1350 story format) via canvas.
 * Loads well-known fonts if available; falls back to system fonts.
 */
async function generatePoster({ name, position, weeklySales, rating, ratingCount, fastAccepts, top10Streak, avgSec, city, appUrl }) {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background — deep black with lime radial glow
  ctx.fillStyle = "#09090B";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H * 0.35, 40, W / 2, H * 0.35, W * 0.7);
  glow.addColorStop(0, "rgba(255, 107, 26, 0.35)");
  glow.addColorStop(1, "rgba(255, 107, 26, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Grid pattern
  ctx.strokeStyle = "rgba(255,255,255,0.03)";
  ctx.lineWidth = 1;
  for (let x = 0; x <= W; x += 60) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
  }
  for (let y = 0; y <= H; y += 60) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  // Logo top
  ctx.fillStyle = "#FF6B1A";
  const logoR = 30;
  ctx.beginPath();
  ctx.roundRect(80, 80, 90, 90, 22);
  ctx.fill();
  ctx.fillStyle = "#000";
  ctx.font = "900 60px 'Cabinet Grotesk', system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("R", 125, 125);

  ctx.fillStyle = "#FAFAFA";
  ctx.font = "900 44px 'Cabinet Grotesk', system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("Rivendas", 200, 115);

  ctx.fillStyle = "#A1A1AA";
  ctx.font = "500 22px 'Plus Jakarta Sans', system-ui, sans-serif";
  ctx.fillText("Ranking semanal · marketplace reverso", 200, 155);

  // Position hero
  if (position) {
    ctx.fillStyle = position === 1 ? "#F59E0B" : position === 2 ? "#D4D4D8" : position === 3 ? "#EA580C" : "#FF6B1A";
    ctx.font = "900 380px 'Cabinet Grotesk', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`#${position}`, W / 2, 470);

    ctx.fillStyle = "#FAFAFA";
    ctx.font = "800 44px 'Cabinet Grotesk', system-ui, sans-serif";
    ctx.fillText("no Top 10 semanal", W / 2, 690);
  } else {
    ctx.fillStyle = "#FF6B1A";
    ctx.font = "900 200px 'Cabinet Grotesk', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🔥", W / 2, 400);
    ctx.fillStyle = "#FAFAFA";
    ctx.font = "800 44px 'Cabinet Grotesk', system-ui, sans-serif";
    ctx.fillText("Rumo ao Top 10", W / 2, 570);
  }

  // Name card
  ctx.fillStyle = "#18181B";
  ctx.beginPath();
  ctx.roundRect(80, 750, W - 160, 180, 24);
  ctx.fill();
  ctx.strokeStyle = "#27272A";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.fillStyle = "#A1A1AA";
  ctx.font = "700 22px 'Plus Jakarta Sans', system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("VENDEDOR", 120, 780);

  ctx.fillStyle = "#FAFAFA";
  ctx.font = "900 56px 'Cabinet Grotesk', system-ui, sans-serif";
  const displayName = name && name.length > 22 ? name.slice(0, 22) + "…" : (name || "—");
  ctx.fillText(displayName, 120, 815);

  if (city) {
    ctx.fillStyle = "#A1A1AA";
    ctx.font = "500 26px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillText(`📍 ${city}`, 120, 885);
  }

  // Stats row
  const stats = [
    { label: "Vendas/7d", value: weeklySales != null ? String(weeklySales) : "0" },
    { label: "Nota", value: rating > 0 ? rating.toFixed(1) : "—" },
    { label: "Resposta", value: avgSec != null ? (avgSec < 60 ? `${Math.round(avgSec)}s` : `${Math.round(avgSec / 60)}min`) : "—" },
  ];

  const boxW = (W - 160 - 40) / 3;
  stats.forEach((s, i) => {
    const x = 80 + i * (boxW + 20);
    ctx.fillStyle = "#18181B";
    ctx.beginPath();
    ctx.roundRect(x, 960, boxW, 180, 20);
    ctx.fill();
    ctx.strokeStyle = "#27272A";
    ctx.stroke();

    ctx.fillStyle = "#FF6B1A";
    ctx.font = "900 68px 'Cabinet Grotesk', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(s.value, x + boxW / 2, 1030);

    ctx.fillStyle = "#A1A1AA";
    ctx.font = "700 22px 'Plus Jakarta Sans', system-ui, sans-serif";
    ctx.fillText(s.label.toUpperCase(), x + boxW / 2, 1100);
  });

  // Badges
  let badgeY = 1180;
  const badges = [];
  if (top10Streak > 1) badges.push({ label: `👑 ${top10Streak}× TOP 10`, color: "#FF6B1A" });
  if (fastAccepts >= 3) badges.push({ label: `🔥 ${fastAccepts}× RÁPIDO`, color: "#FB923C" });
  if (avgSec != null && avgSec < 30) badges.push({ label: "⚡ VENDEDOR RÁPIDO", color: "#FF6B1A" });

  if (badges.length > 0) {
    ctx.font = "800 22px 'Plus Jakarta Sans', system-ui, sans-serif";
    let cursorX = 80;
    badges.forEach((b) => {
      const padding = 24;
      const textW = ctx.measureText(b.label).width;
      const bw = textW + padding * 2;
      if (cursorX + bw > W - 80) return;
      ctx.fillStyle = `${b.color}22`;
      ctx.strokeStyle = b.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(cursorX, badgeY, bw, 52, 26);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = b.color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(b.label, cursorX + bw / 2, badgeY + 27);
      cursorX += bw + 12;
    });
  }

  // Footer CTA
  ctx.fillStyle = "#A1A1AA";
  ctx.font = "500 26px 'Plus Jakarta Sans', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(appUrl.replace(/^https?:\/\//, ""), W / 2, H - 90);

  ctx.fillStyle = "#FAFAFA";
  ctx.font = "800 30px 'Cabinet Grotesk', system-ui, sans-serif";
  ctx.fillText("Quem compra anuncia. Quem vende disputa.", W / 2, H - 140);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png", 0.95);
  });
}

export default function ShareCard({ open, onClose, user, appUrl }) {
  const previewRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = `${appUrl}/u/${user.user_id}`;

  const doShare = async () => {
    setBusy(true);
    try {
      const blob = await generatePoster({
        name: user.name,
        position: user.weekly_position,
        weeklySales: user.weekly_sales,
        rating: user.rating_avg,
        ratingCount: user.rating_count,
        fastAccepts: user.fast_accepts_this_week,
        top10Streak: user.top10_streak,
        avgSec: user.avg_response_seconds,
        city: user.city,
        appUrl,
      });
      const file = new File([blob], `rivendas-${user.user_id}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Meu ranking no Rivendas",
          text: `Confere meu ranking no marketplace reverso do Brasil! 🚀\n${publicUrl}`,
        });
        toast.success("Compartilhado!");
      } else {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rivendas-ranking.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Imagem baixada!");
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Erro ao gerar imagem");
    }
    setBusy(false);
  };

  const doDownload = async () => {
    setBusy(true);
    try {
      const blob = await generatePoster({
        name: user.name,
        position: user.weekly_position,
        weeklySales: user.weekly_sales,
        rating: user.rating_avg,
        ratingCount: user.rating_count,
        fastAccepts: user.fast_accepts_this_week,
        top10Streak: user.top10_streak,
        avgSec: user.avg_response_seconds,
        city: user.city,
        appUrl,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rivendas-ranking-${user.user_id}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Baixado!");
    } catch {
      toast.error("Erro ao gerar imagem");
    }
    setBusy(false);
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Falha ao copiar");
    }
  };

  // Live preview
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const blob = await generatePoster({
        name: user.name,
        position: user.weekly_position,
        weeklySales: user.weekly_sales,
        rating: user.rating_avg,
        ratingCount: user.rating_count,
        fastAccepts: user.fast_accepts_this_week,
        top10Streak: user.top10_streak,
        avgSec: user.avg_response_seconds,
        city: user.city,
        appUrl,
      });
      if (cancelled || !previewRef.current) return;
      const url = URL.createObjectURL(blob);
      previewRef.current.src = url;
    })();
    return () => { cancelled = true; };
  }, [open, user, appUrl]);

  if (!open) return null;

  return (
    <div
      data-testid="share-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md p-5 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-secondary"
          data-testid="share-close"
        >
          <X size={18} />
        </button>
        <h3 className="font-heading font-black text-2xl tracking-tight mb-1">Compartilhar ranking</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Mostra pros amigos que você está bombando no Rivendas 🔥
        </p>

        {/* Preview */}
        <div className="bg-background rounded-xl overflow-hidden border border-border mb-4">
          <img
            ref={previewRef}
            alt="Preview do card"
            data-testid="share-preview"
            className="w-full block"
            style={{ aspectRatio: "1080/1350" }}
          />
        </div>

        <div className="grid grid-cols-1 gap-2">
          <button
            onClick={doShare}
            disabled={busy}
            data-testid="share-native"
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:opacity-90"
          >
            <Share2 size={18} /> {busy ? "Preparando..." : "Compartilhar"}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={doDownload}
              disabled={busy}
              data-testid="share-download"
              className="h-11 rounded-xl border border-border font-medium flex items-center justify-center gap-2 hover:bg-secondary"
            >
              <Download size={16} /> Baixar PNG
            </button>
            <button
              onClick={doCopy}
              data-testid="share-copy-link"
              className="h-11 rounded-xl border border-border font-medium flex items-center justify-center gap-2 hover:bg-secondary"
            >
              {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
              {copied ? "Copiado" : "Copiar link"}
            </button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-3 text-center break-all">{publicUrl}</p>
      </div>
    </div>
  );
}
